import React, { useEffect } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { Box, Typography, IconButton, Skeleton } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { fetchCompanyProfile } from '../../redux/actions/entreprisesAction'
import { useSearchTokens } from '../SearchScreen/searchDesignTokens'
import CompanyHeader from './components/CompanyHeader'
import CompanyTimeline from './components/CompanyTimeline'
import ShareholderTable from './components/ShareholderTable'
import CompanyActivityCard from './components/CompanyActivityCard'
import CompanyLegalCard from './components/CompanyLegalCard'

const CompanyProfileScreen = () => {
  const { rc } = useParams()
  const [searchParams] = useSearchParams()
  const villeRc = searchParams.get('ville_rc')
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const dk = useSearchTokens()

  const profile = useSelector((state) => state.entreprises.companyProfile)
  const isLoading = useSelector((state) => state.entreprises.loading.company)
  const error = useSelector((state) => state.entreprises.errors.company)

  useEffect(() => {
    if (rc) {
      dispatch(fetchCompanyProfile(rc, villeRc))
    }
  }, [rc, villeRc, dispatch])

  if (isLoading) {
    return (
      <Box sx={{ height: '100vh', backgroundColor: dk.bg, overflow: 'auto' }}>
        <Box sx={{ maxWidth: 960, mx: 'auto', p: { xs: 2, md: 4 } }}>
          <Skeleton variant="rounded" width={120} height={32} sx={{ bgcolor: dk.chipBg, mb: 3 }} />
          <Skeleton variant="rounded" width="100%" height={180} sx={{ bgcolor: dk.chipBg, borderRadius: '12px', mb: 3 }} />
          <Skeleton variant="rounded" width="100%" height={120} sx={{ bgcolor: dk.chipBg, borderRadius: '12px', mb: 3 }} />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" width="100%" height={70} sx={{ bgcolor: dk.chipBg, borderRadius: '10px', mb: 2 }} />
          ))}
        </Box>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ height: '100vh', backgroundColor: dk.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ color: dk.error, mb: 2 }}>{error}</Typography>
          <IconButton onClick={() => navigate(-1)} sx={{ color: dk.gold }}>
            <ArrowLeft size={20} />
          </IconButton>
        </Box>
      </Box>
    )
  }

  if (!profile) return null

  const { company, announcements } = profile

  // Use the constitution announcement (or earliest) to pull extra structured fields
  const sorted = [...(announcements || [])].sort(
    (a, b) => new Date(a.date_publication) - new Date(b.date_publication)
  )
  const enrichedAnnonce =
    sorted.find((a) => a.type_annonce === 'constitution') || sorted[0] || null

  return (
    <Box
      sx={{
        height: '100vh',
        backgroundColor: dk.bg,
        overflow: 'auto',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { backgroundColor: dk.scrollThumb, borderRadius: 2 },
      }}
    >
      <Box sx={{ maxWidth: 960, mx: 'auto', p: { xs: 2, md: 4 } }}>
        {/* Back button */}
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            color: dk.text2,
            mb: 2,
            p: 0.8,
            borderRadius: '8px',
            backgroundColor: dk.chipBg,
            '&:hover': { backgroundColor: dk.goldSub, color: dk.gold },
          }}
        >
          <ArrowLeft size={18} />
        </IconButton>

        {/* Company Header Card */}
        <Box data-tour="company-header">
          <CompanyHeader company={company} />
        </Box>

        {/* Shareholders */}
        {company.personnes && company.personnes.length > 0 && (
          <Box data-tour="company-shareholders" sx={{ mt: 3 }}>
            <ShareholderTable personnes={company.personnes} />
          </Box>
        )}

        {/* Activity & Classification */}
        {enrichedAnnonce && (
          <Box sx={{ mt: 3 }}>
            <CompanyActivityCard annonce={enrichedAnnonce} />
          </Box>
        )}

        {/* Legal identifiers & Depot légal */}
        {enrichedAnnonce && (
          <Box sx={{ mt: 3 }}>
            <CompanyLegalCard annonce={enrichedAnnonce} />
          </Box>
        )}

        {/* Timeline */}
        {announcements && announcements.length > 0 && (
          <Box data-tour="company-timeline" sx={{ mt: 3 }}>
            <CompanyTimeline announcements={announcements} />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default CompanyProfileScreen
